import React, { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updatePassword, getCurrentUser } from '@aws-amplify/auth';
import DefaultLayout from '../../layout/DefaultLayout';
import { validatePassword, passwordsMatch, getPasswordRequirementText } from '../../utils/passwordValidation';

const ChangePasswordPage: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await getCurrentUser();
      } catch (error) {
        console.error('Not authenticated', error);
        navigate('/');
      }
    };
    checkAuth();
  }, [navigate]);



  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setMessage('');
    setIsSuccess(false);

    if (!passwordsMatch(newPassword, confirmPassword)) {
      setMessage('新しいパスワードが一致しません');
      setIsLoading(false);
      return;
    }

    const validationResult = validatePassword(newPassword);
    if (!validationResult.isValid) {
      setMessage(validationResult.errorMessage || 'パスワードが無効です');
      setIsLoading(false);
      return;
    }

    try {
      await updatePassword({
        oldPassword: currentPassword,
        newPassword: newPassword,
      });
      setMessage('パスワードが正常に変更されました');
      setIsSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'NotAuthorizedException') {
          setMessage('現在のパスワードが正しくありません');
        } else {
          setMessage('エラーが発生しました: ' + error.message);
        }
      } else {
        setMessage('エラーが発生しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DefaultLayout>
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="border-b border-stroke py-4 px-6.5 dark:border-strokedark">
          <h3 className="font-medium text-black dark:text-white">パスワード変更</h3>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="p-6.5">
            {message && (
              <div
                className={`mb-4.5 p-4 rounded-md ${
                  isSuccess
                    ? 'bg-success/10 text-success border border-success/20'
                    : 'bg-danger/10 text-danger border border-danger/20'
                }`}
              >
                {message}
              </div>
            )}

            <div className="mb-4.5">
              <label className="mb-2.5 block text-black dark:text-white">
                現在のパスワード <span className="text-meta-1">*</span>
              </label>
              <input
                type="password"
                placeholder="現在のパスワードを入力"
                className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-5 text-black outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <div className="mb-4.5">
              <label className="mb-2.5 block text-black dark:text-white">
                新しいパスワード <span className="text-meta-1">*</span>
              </label>
              <input
                type="password"
                placeholder="新しいパスワードを入力"
                className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-5 text-black outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={isLoading}
              />
              <p className="mt-2 text-sm text-body">
                {getPasswordRequirementText()}
              </p>
            </div>

            <div className="mb-6">
              <label className="mb-2.5 block text-black dark:text-white">
                新しいパスワード（確認） <span className="text-meta-1">*</span>
              </label>
              <input
                type="password"
                placeholder="新しいパスワードを再入力"
                className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-5 text-black outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              className="flex w-full justify-center rounded bg-primary p-3 font-medium text-gray hover:bg-opacity-90 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? '変更中...' : 'パスワードを変更'}
            </button>
          </div>
        </form>
      </div>
    </DefaultLayout>
  );
};

export default ChangePasswordPage;